<template>
  <authenticated-layout :back-route="{ name: 'reports' }" :breadcrumbs="breadcrumbItems">
    <page-header
      title="Group Analysis Report"
      description="Examine user engagement and performance metrics across institutions."
    />

    <ReportPeriodFilterBar
      :show-filters="true"
      :show-time-frame="false"
      :show-time-frame-switch="true"
      :show-run-report-button="true"
      @run-report="runReport"
      @reset-filters="resetReport"
    />

    <template v-if="showFilterError">
      <v-alert type="error" class="mx-4" colored-border>
        Please select at least one filter (Tenant, Institution, or Cohort) to run the report.
      </v-alert>
    </template>

    <template v-if="showReport">
      <v-container fluid>
        <v-row>
          <v-col cols="12">
            <v-tabs v-model="activeTab">
              <v-tab>Overview</v-tab>
              <v-tab>Worker Type</v-tab>
              <v-tab>Personality Traits</v-tab>
              <v-tab>Subject Choices</v-tab>
            </v-tabs>
          </v-col>
        </v-row>

        <template v-if="activeTab === 0">
          <v-row>
            <v-col cols="3">
              <v-col>
                <users-signed-up density="default"/>
              </v-col>
              <v-col>
                <access-codes-usage-ratio-metric-tile density="default"/>
              </v-col>
              <v-col>
                <holland-code-complete density="default"/>
              </v-col>
              <v-col>
                <big5-completions density="default"/>
              </v-col>
              <v-col>
                <subject-choices-selected density="default"/>
              </v-col>
            </v-col>
            <v-col cols="9">
              <v-row>
                <v-col cols="12">
                  <HollandCodeDistributionReportGraphs :report-data="hollandCodeDistributionReportData"
                                                       :show-breakdown="false"/>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <PersonalityTraitsDistributionGraphs :report-data="personalityTraitsDistributionReportData"/>
                </v-col>
              </v-row>

            </v-col>
          </v-row>
        </template>
        <template v-if="activeTab === 1">
          <v-card variant="outlined" class="bg-white mt-4">
            <v-row align="center" class="my-1 px-3">
              <v-spacer/>
              <v-col cols="auto">
                <data-export-download-menu
                  :formats="['csv', 'excel']"
                  @click="exportHollandCodeResults"
                  class="mr-2"
                />
              </v-col>
            </v-row>
            <v-divider/>
          </v-card>
          <UserHollandCodesDataTable v-if="userHollandCodesTableData" :loading-data="loadingHollandCodeTable"
                                     :report-data="userHollandCodesTableData"/>
        </template>
        <template v-if="activeTab === 2">
          <v-card variant="outlined" class="bg-white mt-4">
            <v-row align="center" class="my-1 px-3">
              <v-spacer/>
              <v-col cols="auto">
                <data-export-download-menu
                  :formats="['csv', 'excel']"
                  @click="exportPersonalityTraitsData"
                  class="mr-2"
                />
              </v-col>
            </v-row>
            <v-divider/>
          </v-card>
          <UserPersonalityTraitsDataTable v-if="personalityTraitsTableData"
                                          :loading-data="loadingPersonalityTraitsTable"
                                          :report-data="personalityTraitsTableData"/>
        </template>
        <template v-if="activeTab === 3">
          <v-card variant="outlined" class="bg-white mt-4">
            <v-row align="center" class="my-1 px-3">
              <v-spacer/>
              <v-col cols="auto">
                <data-export-download-menu
                  :formats="['csv', 'excel']"
                  @click="exportUserSubjectChoices"
                  class="mr-2"
                />
              </v-col>
            </v-row>
            <v-divider/>
          </v-card>
          <UserSubjectChoicesDataTable v-if="subjectChoicesTableData" :loading-data="loadingSubjectChoicesTable"
                                       :report-data="subjectChoicesTableData"
                                       curriculum="national_senior_certificate"/>
        </template>
      </v-container>
    </template>

  </authenticated-layout>
</template>

<script setup lang="ts">
import AuthenticatedLayout from "@/layout/AuthenticatedLayout.vue";
import PageHeader from "@/components/PageHeader.vue";

import {computed, onMounted, ref, watch} from "vue";
import {useReportingStore} from "@/store/reporting";
import ReportPeriodFilterBar from "@/components/Reporting/ReportPeriodFilterBar.vue";
import AccessCodesUsageRatioMetricTile from "@/components/Metrics/AccessCodesUsageRatioMetricTile.vue";
import UsersSignedUp from "@/components/Metrics/UsersSignedUp.vue";
import HollandCodeComplete from "@/components/Metrics/HollandCodeComplete.vue";
import Big5Completions from "@/components/Metrics/Big5Completions.vue";
import SubjectChoicesSelected from "@/components/Metrics/SubjectChoicesSelected.vue";
import HollandCodeDistributionReportGraphs
  from "@/pages/Insights/ReportsDetailPage/components/HollandCodeDistributionReportGraphs.vue";
import PersonalityTraitsDistributionGraphs
  from "@/pages/Insights/ReportsDetailPage/components/PersonalityTraitsDistributionGraphs.vue";
import UserHollandCodesDataTable from "@/pages/Insights/ReportsDetailPage/components/UserHollandCodesDataTable.vue";
import DataExportDownloadMenu from "@/components/DataExportDownloadMenu.vue";
import {downloadExportedData, type ExportedData} from "@/lib/fileDownload";
import UserPersonalityTraitsDataTable
  from "@/pages/Insights/ReportsDetailPage/components/UserPersonalityTraitsDataTable.vue";
import UserSubjectChoicesDataTable from "@/pages/Insights/ReportsDetailPage/components/UserSubjectChoicesDataTable.vue";

const reportingStore = useReportingStore();
const activeTab = ref(0);
const showReport = ref(false);
const hollandCodeDistributionReportData = ref();
const personalityTraitsDistributionReportData = ref();
const userHollandCodesTableData = ref();
const personalityTraitsTableData = ref();
const subjectChoicesTableData = ref();
const showFilterError = ref(false);
const loadingHollandCodeTable = ref(false);
const loadingPersonalityTraitsTable = ref(false);
const loadingSubjectChoicesTable = ref(false);

reportingStore.resetState();
reportingStore.setAvailableFilters({
  tenants: true,
  institutions: true,
  cohorts: true,
  curriculum: true
});

const breadcrumbItems = computed(() => [
  {text: "Insights", to: {name: "reports"}},
  {text: "License Code Usage"}
]);

onMounted(() => {
  resetReport();
});

// Watch for tab changes and load data lazily
watch(activeTab, async (newTab) => {
  if (!showReport.value) return;

  switch (newTab) {
  case 1: // Worker Type tab
    if (!userHollandCodesTableData.value && !loadingHollandCodeTable.value) {
      loadingHollandCodeTable.value = true;
      try {
        await fetchHollandCodeTableData();
      } finally {
        loadingHollandCodeTable.value = false;
      }
    }
    break;
  case 2: // Personality Traits tab
    if (!personalityTraitsTableData.value && !loadingPersonalityTraitsTable.value) {
      loadingPersonalityTraitsTable.value = true;
      try {
        await fetchPersonalityTraitsTableData();
      } finally {
        loadingPersonalityTraitsTable.value = false;
      }
    }
    break;
  case 3: // Subject Choices tab
    if (!subjectChoicesTableData.value && !loadingSubjectChoicesTable.value) {
      loadingSubjectChoicesTable.value = true;
      try {
        await fetchSubjectChoicesTableData();
      } finally {
        loadingSubjectChoicesTable.value = false;
      }
    }
    break;
  }
});

async function fetchHollandCodeDistribution() {
  hollandCodeDistributionReportData.value = await reportingStore.fetchReport("holland-code-distribution");
}

async function fetchPersonalityTraitsDistribution() {
  personalityTraitsDistributionReportData.value = await reportingStore.fetchReport("personality-traits-distribution");
}

async function fetchHollandCodeTableData() {
  userHollandCodesTableData.value = await reportingStore.fetchReport("user-worker-type-results");
}

async function fetchPersonalityTraitsTableData() {
  personalityTraitsTableData.value = await reportingStore.fetchReport("big5-results");
}

async function fetchSubjectChoicesTableData() {
  subjectChoicesTableData.value = await reportingStore.fetchReport("user-subject-choices");
}

async function exportHollandCodeResults(format: "csv" | "excel") {
  const exportData = await reportingStore.fetchReport("user-worker-type-results", {
    exportFormat: format
  });
  downloadExportedData(exportData as unknown as ExportedData);
}

async function exportPersonalityTraitsData(format: "csv" | "excel") {
  const exportData = await reportingStore.fetchReport("big5-results", {
    exportFormat: format
  });
  downloadExportedData(exportData as unknown as ExportedData);
}

async function exportUserSubjectChoices(format: "csv" | "excel") {
  const exportData = await reportingStore.fetchReport("user-subject-choices", {
    exportFormat: format
  });
  downloadExportedData(exportData as unknown as ExportedData);
}

async function runReport() {
  reportingStore.activateAndRefresh();
  showFilterError.value = false;

  const activeFilters = reportingStore.getActiveFilters;

  if (activeFilters.tenants.length === 0 &&
    activeFilters.institutions.length === 0 &&
    activeFilters.cohorts.length === 0) {
    // Require at least one filter to be selected
    showFilterError.value = true;
    return;
  }

  // Only load overview data initially
  await fetchHollandCodeDistribution();
  await fetchPersonalityTraitsDistribution();
  showReport.value = true;
}

function resetReport() {
  showFilterError.value = false;
  showReport.value = false;
  hollandCodeDistributionReportData.value = null;
  personalityTraitsDistributionReportData.value = null;
  userHollandCodesTableData.value = null;
  personalityTraitsTableData.value = null;
  subjectChoicesTableData.value = null;
  loadingHollandCodeTable.value = false;
  loadingPersonalityTraitsTable.value = false;
  loadingSubjectChoicesTable.value = false;
  activeTab.value = 0;
  reportingStore.resetFilters();
}

</script>
